import React, { useState, useEffect, useRef } from "react";
import { estadoLabel } from "../constants/estadosAcademicos";
import { nombreCompleto } from "../adapters/estudiantesAdapter";
import { isModuloHabilitado } from "../../../../utils/modulosSistema";

export default function EstudiantesTable({
  estudiantes,
  loading,
  reload,
  error,
  seleccionadoId,
  onSelect,
  onEdit,
  onDelete,
  maxHeight = "60vh",
  onViewDocs,
  onViewDetail,
  onSyncContifico,
  onCampusReset,
  canEdit = true,
  canDelete = true,
}) {
  const contificoActivo = isModuloHabilitado("integracion_contifico");
  const minRows = 10;
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const wrapRef = useRef(null);

  useEffect(() => {
    const handleOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  if (loading) {
    return (
      <div className="management-card text-center py-5">
        <span className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="management-card management-alert">
        {error}
      </div>
    );
  }

  if (!estudiantes.length) {
    return (
      <div className="management-card dashboard-empty text-center py-5">
        <i className="bi bi-journal-text dashboard-empty-icon d-block mb-3" />
        <h5 className="fw-semibold mb-1">No hay estudiantes registrados.</h5>
        <p className="text-muted mb-0">Crea un estudiante o ajusta los filtros.</p>
      </div>
    );
  }

  const emptyRows = Math.max(0, minRows - estudiantes.length);
  const hasActions = onViewDetail || onViewDocs || canEdit || canDelete;

  return (
    <div className="management-card dashboard-table-card estudiantes-table-card">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <p className="dashboard-label mb-1">Listado</p>
          <h6 className="mb-0">Estudiantes registrados</h6>
        </div>
        {reload && (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={reload}
            disabled={loading}
          >
            <i className="bi bi-arrow-repeat me-2" />
            Actualizar
          </button>
        )}
      </div>
      <div className="management-table-wrapper" style={{ maxHeight }}>
        <table className="table align-middle management-table estudiantes-table mb-0">
          <thead className="bg-transparent">
            <tr>
              <th>#</th>
              <th>Estudiante</th>
              <th>Documento</th>
              <th>Contacto</th>
              <th>Estado</th>
              {hasActions && <th className="text-end">Acciones</th>}
            </tr>
          </thead>
          <tbody ref={wrapRef}>
            {estudiantes.map((est, idx) => {
              const isSelected = seleccionadoId === est.id;
              const rowKey = est.id !== undefined && est.id !== null ? `est-${est.id}-${idx}` : `est-temp-${idx}`;
              const isMenuOpen = openMenuId === est.id;
              return (
                <tr
                  key={rowKey}
                  className={isSelected ? "selected-row" : ""}
                  onClick={() => onSelect?.(est)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelect?.(est);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                >
                  <td className="fw-semibold">{idx + 1}</td>
                  <td
                    onClick={(e) => { e.stopPropagation(); onViewDetail?.(est); }}
                    style={{ cursor: "pointer" }}
                    title="Ver detalle del estudiante"
                  >
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-semibold">{nombreCompleto(est) || "Sin nombre"}</span>
                      {contificoActivo && est.contificoPersonaId && (
                        <i className="bi bi-cloud-check text-success" title="Sincronizado con Contífico" style={{ fontSize: 13 }} />
                      )}
                    </div>
                    {est.referenteNombre && (
                      <small className="text-muted">
                        <i className="bi bi-person-check me-1" />
                        Referido por: {est.referenteNombre}
                      </small>
                    )}
                  </td>
                  <td>{est.documento || "N/D"}</td>
                  <td>
                    <div>{est.telefono || "Sin telefono"}</div>
                  </td>
                  <td>
                    <div className="d-flex flex-wrap gap-1">
                      <span className="badge rounded-pill bg-light text-dark border fw-semibold">
                        {estadoLabel(est.estadoAcademico)}
                      </span>
                      <span className={`badge rounded-pill px-3 py-2 ${est.activo ? "bg-success-subtle text-success" : "bg-secondary-subtle text-muted"}`}>
                        {est.activo ? "Activo" : "Inactivo"}
                      </span>
                    </div>
                  </td>
                  {hasActions && (
                    <td className="text-end">
                      <div className="management-actions-wrap">
                        <button
                          type="button"
                          className={`management-actions-toggle${isMenuOpen ? " is-open" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isMenuOpen) {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                            }
                            setOpenMenuId(isMenuOpen ? null : est.id);
                          }}
                          title="Acciones"
                          aria-expanded={isMenuOpen}
                        >
                          <i className="bi bi-three-dots-vertical" />
                        </button>
                        {isMenuOpen && (
                          <ul className="management-actions-menu" style={{ position: "fixed", top: menuPos.top, right: menuPos.right, left: "auto" }}>
                            {onViewDetail && (
                              <li>
                                <button
                                  type="button"
                                  className="menu-item"
                                  onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); onViewDetail(est); }}
                                >
                                  <i className="bi bi-person-lines-fill" />
                                  Detalle
                                </button>
                              </li>
                            )}
                            {onViewDocs && (
                              <li>
                                <button
                                  type="button"
                                  className="menu-item"
                                  onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); onViewDocs(est); }}
                                >
                                  <i className="bi bi-folder2-open" />
                                  Documentos
                                </button>
                              </li>
                            )}
                            {contificoActivo && onSyncContifico && !est.contificoPersonaId && (
                              <li>
                                <button
                                  type="button"
                                  className="menu-item"
                                  onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); onSyncContifico(est); }}
                                >
                                  <i className="bi bi-cloud-upload" />
                                  Sincronizar con Contífico
                                </button>
                              </li>
                            )}
                            {(onViewDetail || onViewDocs || (contificoActivo && onSyncContifico && !est.contificoPersonaId)) && (canEdit || canDelete) && (
                              <li className="menu-divider" />
                            )}
                            {onCampusReset && (
                              <li>
                                <button
                                  type="button"
                                  className="menu-item"
                                  onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); onCampusReset(est); }}
                                >
                                  <i className="bi bi-key" />
                                  Resetear acceso Campus
                                </button>
                              </li>
                            )}
                            {canEdit && (
                              <li>
                                <button
                                  type="button"
                                  className="menu-item"
                                  onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); onEdit?.(est); }}
                                >
                                  <i className="bi bi-pencil-square" />
                                  Editar
                                </button>
                              </li>
                            )}
                            {canDelete && (
                              <li>
                                <button
                                  type="button"
                                  className="menu-item is-danger"
                                  onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); onDelete?.(est); }}
                                >
                                  <i className="bi bi-trash" />
                                  Eliminar
                                </button>
                              </li>
                            )}
                          </ul>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
            {emptyRows > 0 &&
              Array.from({ length: emptyRows }).map((_, idx) => (
                <tr key={`est-empty-${idx}`} className="table-empty-row" aria-hidden="true">
                  <td colSpan={hasActions ? 6 : 5}>&nbsp;</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
